import { Navigate } from 'react-router-dom'
import { Box } from '@mantine/core'
import { useForm, zodResolver } from '@mantine/form'
import { useIsMutating } from '@tanstack/react-query'

import { LoginRoutePathsEnum } from '@/modules/login/routes/loginRouteConfig.types'
import { passwordResetFormSchema } from '@/modules/passwordReset/components/PasswordResetForm/PasswordResetForm.schema'
import type {
  PasswordResetFormProps,
  PasswordResetFormSchema,
} from '@/modules/passwordReset/components/PasswordResetForm/PasswordResetForm.types'
import { passwordResetFormDefaultValues } from '@/modules/passwordReset/components/PasswordResetForm/PasswordResetForm.values'

import { useUserStore } from '@/store/user.store'

import { Anchor } from '@/components/Anchor/Anchor'
import { Button } from '@/components/Button/Button'
import { TextInput } from '@/components/TextInput/TextInput'

import { useRolanWealthServiceAuthControllerResetPassword } from '@/__generated__/queries'

import { getUserDefaultPath } from '@/helpers/getUserDefaultPath.helper'

export function PasswordResetForm({
  setSuccessMessage,
}: PasswordResetFormProps) {
  const user = useUserStore.use.user()
  const currentRole = useUserStore.use.currentRole()

  const isMutating = useIsMutating({
    exact: true,
  })

  const form = useForm<PasswordResetFormSchema>({
    initialValues: passwordResetFormDefaultValues,
    validate: zodResolver(passwordResetFormSchema),
  })

  const passwordResetMutation =
    useRolanWealthServiceAuthControllerResetPassword({
      onSuccess: data => {
        setSuccessMessage(data.message)
      },
    })

  const handleSubmit = form.onSubmit(values => {
    passwordResetMutation.mutate({
      requestBody: values,
    })
  })

  if (user) {
    return <Navigate to={getUserDefaultPath(user, currentRole)} />
  }

  return (
    <Box component="form" noValidate onSubmit={handleSubmit}>
      <Box mt={48}>
        <TextInput
          label="Email"
          placeholder="Enter your email address"
          {...form.getInputProps('email')}
        />
      </Box>

      <Box style={{ textAlign: 'right' }}>
        <Anchor href={LoginRoutePathsEnum.LOGIN} variant="secondary">
          Sign in
        </Anchor>
      </Box>

      <Box mt={56}>
        <Button loading={!!isMutating} type="submit">
          Reset Password
        </Button>
      </Box>
    </Box>
  )
}
